import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import type {
  NewsBeatDetail,
  NewsBriefingTags,
  NewsTagStat,
} from '../../types';

/**
 * What the tagger noticed in this run, and what the beat did with it.
 *
 * Every article is already tagged with who / what / where — that is how three
 * outlets on one outage collapse into one story — and until now none of it was
 * visible. A user looking at "47 filtered out" had no way to see that 11 of the
 * 47 were about Nevada, which is the fact that writes the filter rule.
 *
 * So each chip carries a kept-versus-dropped bar and, behind a click, the two
 * moves that fact implies: make it a filter value, or make it a search term.
 * Both go through the write paths the Setup form already uses — this screen
 * knows nothing about how attributes are stored.
 *
 * The third action is the point of the whole thing: after either write, offer
 * to re-process. Change a rule, judge the SAME articles again, see whether it
 * helped — without leaving the tab or spending a news-API request.
 */

function Bar({ tag }: { tag: NewsTagStat }) {
  const kept = tag.in_stories;
  const dropped = tag.dropped;
  const other = Math.max(tag.articles - kept - dropped, 0);
  const pct = (n: number) => `${(n / Math.max(tag.articles, 1)) * 100}%`;
  return (
    <span
      className="ml-1.5 inline-flex h-1.5 w-10 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700 align-middle"
      title={`${kept} in stories · ${dropped} dropped · ${other} other`}
    >
      <span className="bg-emerald-500" style={{ width: pct(kept) }} />
      <span className="bg-gray-400 dark:bg-gray-500" style={{ width: pct(dropped) }} />
    </span>
  );
}

export default function BriefingTags({
  beat,
  briefingId,
  reloadKey,
  onChanged,
  onReprocess,
}: {
  beat: NewsBeatDetail;
  briefingId: number;
  /** Bumped by the parent when a run lands, so the counts re-read. */
  reloadKey?: number;
  onChanged: (next: NewsBeatDetail) => void;
  /** Absent on a live beat, where re-processing cannot run. */
  onReprocess?: (briefingId: number) => void;
}) {
  const [data, setData] = useState<NewsBriefingTags | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.getNewsBriefingTags(briefingId));
    } catch {
      setData(null);
    }
  }, [briefingId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const filters = beat.attributes.filter((a) => a.role === 'FILTER');

  /** Append a value to a filter attribute, or mint one named after the axis. */
  const asFilterValue = async (
    tag: NewsTagStat,
    dimension: string,
    attributeName: string,
  ) => {
    setBusy(true);
    setError(null);
    try {
      const name = (attributeName || dimension.toLowerCase()).trim();
      const existing = beat.attributes.find(
        (a) => a.name.toLowerCase() === name.toLowerCase(),
      );
      // replace_attributes takes the WHOLE set, so send it all back with the
      // one change folded in. Same shape AttributeEditor sends.
      const next = existing
        ? beat.attributes.map((a) =>
            a === existing
              ? {
                  ...a,
                  role: 'FILTER' as const,
                  values: Array.from(new Set([...(a.values ?? []), tag.value])),
                }
              : a,
          )
        : [
            ...beat.attributes,
            {
              name,
              role: 'FILTER' as const,
              values: [tag.value],
              hint: '',
              sort_order: beat.attributes.length,
            },
          ];
      onChanged(await api.replaceNewsBeatAttributes(beat.id, next));
      setNote(`\`${tag.value}\` is now an allowed value of \`${name}\`.`);
      setActive(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const asSearchTerm = async (tag: NewsTagStat) => {
    setBusy(true);
    setError(null);
    try {
      // clean_keywords dedupes case-insensitively server-side, so sending a
      // term the beat already has is a no-op rather than an error.
      onChanged(
        await api.updateNewsBeat(beat.id, {
          keywords: [...beat.keywords, tag.value],
        }),
      );
      setNote(`\`${tag.value}\` added to the search terms.`);
      setActive(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data || data.dimensions.length === 0) return null;
  const total = data.dimensions.reduce((n, d) => n + d.tags.length, 0);

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left"
      >
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          What the tagger noticed
        </span>
        <span className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span>{total} tags</span>
          <span className="text-gray-400">{open ? '▴' : '▾'}</span>
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-3 space-y-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Read out of every article before it was judged - this is what decides
            which articles are the same story. The bar shows how many ended up in
            a story (green) against how many were dropped (gray). A tag that is
            mostly dropped is a filter you are applying by hand.
          </p>

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          )}
          {note && (
            <p className="flex flex-wrap items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
              <span>{note}</span>
              {onReprocess && (
                <button
                  onClick={() => {
                    setNote(null);
                    onReprocess(briefingId);
                  }}
                  className="underline hover:no-underline"
                >
                  Re-judge the same articles to see the effect
                </button>
              )}
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            {data.dimensions.map((dim) => (
              <div key={dim.dimension}>
                <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                  {dim.label}
                </h4>
                <ul className="space-y-1">
                  {dim.tags.map((tag) => {
                    const key = `${dim.dimension}:${tag.tag_id}`;
                    return (
                      <li key={key}>
                        <button
                          onClick={() => setActive(active === key ? null : key)}
                          className={`w-full text-left text-xs px-1.5 py-0.5 rounded ${
                            active === key
                              ? 'bg-indigo-50 dark:bg-indigo-900/30'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          <span className="text-gray-800 dark:text-gray-200">
                            {tag.value}
                          </span>
                          <span className="text-gray-400 dark:text-gray-500">
                            {' '}
                            · {tag.articles}
                          </span>
                          <Bar tag={tag} />
                        </button>

                        {active === key && (
                          <div className="mt-1 mb-2 ml-1.5 space-y-1 border-l border-gray-200 dark:border-gray-700 pl-2">
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                              {tag.in_stories} in stories · {tag.dropped} dropped
                              {tag.muted > 0 && ` · ${tag.muted} already reported`}
                              {tag.unreviewed > 0 && ` · ${tag.unreviewed} not judged`}
                            </p>
                            {filters.map((f) => (
                              <button
                                key={f.name}
                                disabled={busy}
                                onClick={() =>
                                  asFilterValue(tag, dim.dimension, f.name)
                                }
                                className="block text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
                              >
                                Allow in <code>{f.name}</code>
                              </button>
                            ))}
                            <button
                              disabled={busy}
                              onClick={() => asFilterValue(tag, dim.dimension, '')}
                              className="block text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
                            >
                              New <code>{dim.dimension.toLowerCase()}</code> filter
                              with this value
                            </button>
                            <button
                              disabled={busy}
                              onClick={() => asSearchTerm(tag)}
                              className="block text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
                            >
                              Add as a search term
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
